import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import useAuthStore from "./store/authStore";
import { Sentry } from "./observability/sentry";
import ChangePasswordModal from "./components/ChangePasswordModal";

// Pages
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import SendingConsole from "./pages/SendingConsole";
import ReplyInbox from "./pages/ReplyInbox";
import CampaignDetail from "./pages/CampaignDetail";
import TexterDashboard from "./pages/TexterDashboard";
import CostDashboard from "./pages/CostDashboard";
import ABTestResults from "./pages/ABTestResults";
import ExportCenter from "./pages/ExportCenter";
import ContactLists from "./pages/ContactLists";
import NumberPools from "./pages/NumberPools";
import TagSummaryDashboard from "./pages/TagSummaryDashboard";
import ComplianceDashboard from "./pages/ComplianceDashboard";
import Admin from "./pages/Admin";

function ProtectedRoute({ children, roles }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" />;
  if (roles && user && !roles.includes(user.role)) return <Navigate to="/dashboard" />;

  return children;
}

function Navigation() {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const [showChangePassword, setShowChangePassword] = useState(false);

  if (!user) return null;

  const links = [
    { to: "/dashboard", label: "Dashboard", roles: ["admin", "manager", "texter"] },
    { to: "/send", label: "Send", roles: ["admin", "manager", "texter"] },
    { to: "/replies", label: "Replies", roles: ["admin", "manager", "texter"] },
    { to: "/campaigns", label: "Campaigns", roles: ["admin", "manager"] },
    { to: "/contacts", label: "Contacts", roles: ["admin", "manager"] },
    { to: "/number-pools", label: "Numbers", roles: ["admin", "manager"] },
    { to: "/survey-tags", label: "Tags", roles: ["admin", "manager"] },
    { to: "/ab-tests", label: "A/B Tests", roles: ["admin", "manager"] },
    { to: "/performance", label: "Performance", roles: ["admin", "manager"] },
    { to: "/costs", label: "Costs", roles: ["admin", "manager"] },
    { to: "/compliance", label: "Compliance", roles: ["admin", "manager"] },
    { to: "/exports", label: "Exports", roles: ["admin", "manager"] },
    { to: "/admin", label: "Admin", roles: ["admin"] },
  ].filter((l) => l.roles.includes(user.role));

  return (
    <nav className="bg-white border-b border-gray-200 px-6 py-3">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center space-x-8">
          <span className="text-lg font-bold text-blue-700">Conduit</span>
          <div className="flex space-x-1">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname.startsWith(link.to)
                    ? "bg-blue-50 text-blue-700"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500">
            {user.name} <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{user.role}</span>
          </span>
          <button
            onClick={() => setShowChangePassword(true)}
            className="text-sm text-gray-500 hover:text-blue-700 transition-colors"
          >
            Change Password
          </button>
          <button
            onClick={logout}
            className="text-sm text-gray-500 hover:text-red-600 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </nav>
  );
}

// Fallback UI shown by Sentry's ErrorBoundary when a React subtree
// throws. Kept deliberately small and dependency-free — if the main
// tree is broken, we don't want the fallback to depend on the same
// stack. The reload button is the most useful out is most cases
// (a transient SPA state corruption clears on reload).
function ErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          Something went wrong.
        </h1>
        <p className="text-sm text-gray-600 mb-6">
          The error has been reported. Try reloading the page; if it
          keeps happening, contact support.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Sentry.ErrorBoundary renders `fallback` when a child throws
  // synchronously during render. It also reports the error via the
  // configured Sentry hub — no-op when DSN isn't set, so safe in dev.
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <BrowserRouter>
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <main className="max-w-7xl mx-auto px-6 py-8">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/send" element={<ProtectedRoute><SendingConsole /></ProtectedRoute>} />
            <Route path="/replies" element={<ProtectedRoute><ReplyInbox /></ProtectedRoute>} />
            <Route path="/campaigns" element={<ProtectedRoute roles={["admin", "manager"]}><CampaignDetail /></ProtectedRoute>} />
            <Route path="/campaigns/:id" element={<ProtectedRoute roles={["admin", "manager"]}><CampaignDetail /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute roles={["admin", "manager"]}><ContactLists /></ProtectedRoute>} />
            <Route path="/number-pools" element={<ProtectedRoute roles={["admin", "manager"]}><NumberPools /></ProtectedRoute>} />
            <Route path="/survey-tags" element={<ProtectedRoute roles={["admin", "manager"]}><TagSummaryDashboard /></ProtectedRoute>} />
            <Route path="/ab-tests" element={<ProtectedRoute roles={["admin", "manager"]}><ABTestResults /></ProtectedRoute>} />
            <Route path="/performance" element={<ProtectedRoute roles={["admin", "manager"]}><TexterDashboard /></ProtectedRoute>} />
            <Route path="/costs" element={<ProtectedRoute roles={["admin", "manager"]}><CostDashboard /></ProtectedRoute>} />
            <Route path="/compliance" element={<ProtectedRoute roles={["admin", "manager"]}><ComplianceDashboard /></ProtectedRoute>} />
            <Route path="/exports" element={<ProtectedRoute roles={["admin", "manager"]}><ExportCenter /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><Admin /></ProtectedRoute>} />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
